/**
 * The manual at /learn (boards learn-r1 A and learn-r2): the API's recipe routes and the choices the Learn pages make.
 * The recipes come from one source, the starter template's src/learn/manual, served by GET /api/learn/recipes
 * (../../api/src/domain/manual.ts is the authority; keep these in step). Pure apart from the schemas.
 */
import { z } from 'zod'

const Source = z.object({ repo: z.string().nullable(), commit: z.string().nullable(), fetchedAt: z.string() })

export const RecipeSummary = z.object({
  slug: z.string(),
  group: z.enum(['recipes', 'crash-course']),
  order: z.number(),
  number: z.string(),
  title: z.string(),
  summary: z.string(),
  skill: z.string().nullable(),
  coming: z.string().nullable(),
})
export type RecipeSummary = z.infer<typeof RecipeSummary>

export const Recipe = RecipeSummary.extend({
  prompt: z.string(),
  why: z.string().nullable(),
  steps: z.array(z.string()),
  worked: z.string(),
  wrong: z.array(z.object({ symptom: z.string(), say: z.string() })),
  founder: z.string().nullable(),
  missions: z.array(z.object({ id: z.string(), number: z.string(), title: z.string() })),
})
export type Recipe = z.infer<typeof Recipe>

export const RecipeList = z.object({ recipes: z.array(RecipeSummary), source: Source })
export const RecipeEnvelope = z.object({ recipe: Recipe, source: Source })

export const RECIPES_PATH = '/api/learn/recipes'
export const recipePath = (slug: string) => `${RECIPES_PATH}/${slug.split('/').map(encodeURIComponent).join('/')}`

/** The manual's two parts, in order: the recipes, then the crash course. */
export function groups(list: RecipeSummary[]): { key: RecipeSummary['group']; title: string; sentence: string; items: RecipeSummary[] }[] {
  const by = (g: RecipeSummary['group']) => list.filter((r) => r.group === g).sort((a, b) => a.order - b.order)
  return [
    { key: 'recipes' as const, title: 'Recipes', sentence: 'One thing to say to your agent, and what happens next.', items: by('recipes') },
    { key: 'crash-course' as const, title: 'Crash course', sentence: 'How the pieces under your app work: git, the preview, shipping, dev and prod, good habits.', items: by('crash-course') },
  ].filter((g) => g.items.length > 0)
}

/** Every word of the query appears in the number, title, summary or skill. Empty query: everything. */
export function searchRecipes(list: RecipeSummary[], query: string): RecipeSummary[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (!words.length) return list
  return list.filter((r) => {
    const hay = `${r.number} ${r.title} ${r.summary} ${r.skill ?? ''} ${r.slug}`.toLowerCase()
    return words.every((w) => hay.includes(w))
  })
}

/** What the dashboard already knows about the founder, for picking what to read next. */
export interface LearnState {
  /** The preview address answers: the computer is ready to build on. */
  previewReady: boolean
  /** Shopify is connected; undefined while unknown. */
  shopifyConnected: boolean | undefined
}

/**
 * The dashboard's Learn card: up to three recipes, picked from state the dashboard already has (board learn-r1 A).
 * The first is the one to do next. Recipes waiting on a feature are never picked.
 */
export function pickRecipes(list: RecipeSummary[], state: LearnState, max = 3): RecipeSummary[] {
  const order: string[] = []
  if (!state.previewReady) order.push('what-your-agent-can-do', 'look-before-its-live')
  if (state.shopifyConnected === false) order.push('connect-shopify')
  order.push('ask-for-a-screen', 'look-before-its-live', 'make-it-live', 'undo', 'bring-a-file', 'keep-your-app-tidy')
  const open = new Map(list.filter((r) => !r.coming).map((r) => [r.slug, r]))
  const picked: RecipeSummary[] = []
  for (const slug of order) {
    const r = open.get(slug)
    if (r && !picked.includes(r)) picked.push(r)
    if (picked.length === max) break
  }
  return picked
}

/** Recipe text is plain words with `backticks` for code: the pieces, in order, with which are code. */
export function codeParts(text: string): { text: string; code: boolean }[] {
  return text
    .split(/(`[^`]+`)/)
    .filter(Boolean)
    .map((p) => (p.startsWith('`') && p.endsWith('`') && p.length > 2 ? { text: p.slice(1, -1), code: true } : { text: p, code: false }))
}
